import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'twin.macro';
import Checkbox from 'components/checkbox/Checkbox';
import Dropdown from 'components/dropdown/Dropdown';
import Input from 'components/form/input/Input';
import Label from 'components/form/Label';
import { Error, Title3 } from 'components/typography/Typography';
import useVacancyContext from 'hooks/context/vacancy-context';
import { PayPeriod, VacancySalaryFields } from 'types/vacancyTypes';
import { CaretDown, CaretRight, Trash } from '@phosphor-icons/react';
import {
  CURRENCY_OPTIONS,
  DEFAULT_SALARY_CURRENCY,
  PAY_PERIOD_OPTIONS,
  centsToInputValue,
  formatSalaryDisplay,
  getEmptySalaryFields,
  normalizeSalaryForSubmit,
  parseSalaryInput,
  validateSalary,
} from 'utils/salaryHelpers';

const DEFAULT_PAY_PERIOD: PayPeriod = 'year';

/** The salary as the page holds it, as one comparable value. */
const salaryKey = (fields?: Partial<VacancySalaryFields> | null): string =>
  JSON.stringify([
    fields?.salary_min ?? null,
    fields?.salary_max ?? null,
    fields?.currency ?? null,
    fields?.pay_period ?? null,
    fields?.salary_note ?? null,
  ]);

interface SalaryEditorProps {
  mandatory?: boolean;
  validationErrors?: Partial<Record<keyof VacancySalaryFields, string>>;
}

const SalaryEditor: React.FC<SalaryEditorProps> = ({
  mandatory = false,
  validationErrors,
}) => {
  const { copyData, setCopyData } = useVacancyContext();
  const [rangeEnabled, setRangeEnabled] = useState(false);
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_SALARY_CURRENCY);
  const [payPeriod, setPayPeriod] = useState<PayPeriod>(DEFAULT_PAY_PERIOD);
  const [salaryNote, setSalaryNote] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof VacancySalaryFields, string>>
  >({});

  /*
   * The boxes hold what is being TYPED ("45." is not yet an amount), so they
   * cannot simply mirror the page. They used to read the page only when the
   * campaign changed, which meant an Undo put the earlier salary back in what
   * is kept while the boxes went on showing the newer one.
   *
   * So the salary these boxes last wrote is remembered. Their own echo is
   * ignored; a salary that comes from anywhere else -- an undo, a redo, a
   * reload -- is read back into the boxes.
   */
  const lastWrittenRef = useRef<string | null>(null);
  const pageSalaryKey = `${copyData?.id ?? ''}:${salaryKey(copyData)}`;

  useEffect(() => {
    if (lastWrittenRef.current === pageSalaryKey) return;
    lastWrittenRef.current = pageSalaryKey;

    const hasRange =
      copyData?.salary_min !== null &&
      copyData?.salary_min !== undefined &&
      copyData?.salary_max !== copyData?.salary_min;

    setRangeEnabled(hasRange);
    setSalaryMin(centsToInputValue(copyData?.salary_min));
    setSalaryMax(hasRange ? centsToInputValue(copyData?.salary_max) : '');
    setCurrency(copyData?.currency ?? DEFAULT_SALARY_CURRENCY);
    setPayPeriod(copyData?.pay_period ?? DEFAULT_PAY_PERIOD);
    setSalaryNote(copyData?.salary_note ?? '');
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyData?.id, pageSalaryKey]);

  useEffect(() => {
    if (!validationErrors) return;
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) setCollapsed(false);
  }, [validationErrors]);

  const unreadable = (typed: string) =>
    typed.trim() !== '' && parseSalaryInput(typed).value === null;
  const holdsUnreadableAmount =
    unreadable(salaryMin) || (rangeEnabled && unreadable(salaryMax));

  const previewSalary = useMemo(() => {
    const result = normalizeSalaryForSubmit({
      salary_min: salaryMin,
      salary_max: salaryMax,
      currency,
      pay_period: payPeriod,
      salary_note: salaryNote,
      rangeEnabled,
      allowFromSalary: true,
    });

    if (Object.keys(result.errors).length) return '';
    return formatSalaryDisplay(
      result.values,
      copyData?.salary_metadata?.display,
      {
        includeLabel: true,
      }
    );
  }, [
    copyData?.salary_metadata?.display,
    currency,
    payPeriod,
    rangeEnabled,
    salaryMax,
    salaryMin,
    salaryNote,
  ]);

  const applySalaryChange = ({
    nextSalaryMin = salaryMin,
    nextSalaryMax = salaryMax,
    nextCurrency = currency,
    nextPayPeriod = payPeriod,
    nextSalaryNote = salaryNote,
    nextRangeEnabled = rangeEnabled,
  }: {
    nextSalaryMin?: string;
    nextSalaryMax?: string;
    nextCurrency?: string;
    nextPayPeriod?: PayPeriod;
    nextSalaryNote?: string;
    nextRangeEnabled?: boolean;
  }) => {
    const result = validateSalary({
      salary_min: nextSalaryMin,
      salary_max: nextSalaryMax,
      currency: nextCurrency,
      pay_period: nextPayPeriod,
      salary_note: nextSalaryNote,
      rangeEnabled: nextRangeEnabled,
      mandatory,
    });

    setErrors(result.errors);
    const written = normalizeSalaryForSubmit({
      salary_min: nextSalaryMin,
      salary_max: nextSalaryMax,
      currency: nextCurrency,
      pay_period: nextPayPeriod,
      salary_note: nextSalaryNote,
      rangeEnabled: nextRangeEnabled,
      allowFromSalary: true,
    }).values;
    lastWrittenRef.current = `${copyData?.id ?? ''}:${salaryKey(written)}`;
    setCopyData((prev) => ({
      ...prev!,
      ...written,
    }));
  };

  const handleRangeChange = (checked: boolean) => {
    const nextSalaryMax = checked ? '' : salaryMin;
    setRangeEnabled(checked);
    setSalaryMax(nextSalaryMax);
    applySalaryChange({
      nextRangeEnabled: checked,
      nextSalaryMax,
    });
  };

  const handleRemoveSalary = () => {
    setRangeEnabled(false);
    setSalaryMin('');
    setSalaryMax('');
    setCurrency(DEFAULT_SALARY_CURRENCY);
    setPayPeriod(DEFAULT_PAY_PERIOD);
    setSalaryNote('');
    setErrors({});
    lastWrittenRef.current = `${copyData?.id ?? ''}:${salaryKey(
      getEmptySalaryFields()
    )}`;
    setCopyData((prev) => ({
      ...prev!,
      ...getEmptySalaryFields(),
    }));
  };

  return (
    <section
      tw="shrink-0 bg-white border-b border-gray-200 px-6 py-2"
      data-testid="salary-editor"
      /*
       * The salary is part of what this screen keeps, so Ctrl/Cmd+Z inside
       * these boxes steps the screen back like the Undo arrow (useUndoRedo,
       * `takesPageUndo`). Left to the browser, undoing "4444" typed in one go
       * left one "4" behind (Steven, by hand, 3 Oct 2026).
       *
       * Not while a box holds something the page cannot read ("abc", or
       * "45." on the way to "45.000"): the page knows nothing of those
       * characters, so its undo would take back something else on the screen
       * and leave them standing (review round 4, A8). Then the browser's own
       * undo applies.
       */
      data-page-undo="inputs"
      data-native-undo={holdsUnreadableAmount ? '' : undefined}
    >
      <div tw="max-w-6xl mx-auto">
        <div tw="flex flex-col gap-3">
          <div tw="flex items-center">
            <button
              type="button"
              tw="flex items-center gap-2 text-gray-900 hover:text-gray-700 focus:outline-none"
              aria-expanded={!collapsed}
              aria-controls="salary-editor-fields"
              data-testid="salary-editor-toggle"
              onClick={() => setCollapsed((prev) => !prev)}
            >
              {collapsed ? (
                <CaretRight size={18} weight="bold" />
              ) : (
                <CaretDown size={18} weight="bold" />
              )}
              <Title3>Salary</Title3>
            </button>
          </div>

          {!collapsed && (
            <div
              id="salary-editor-fields"
              tw="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] gap-3 items-start"
            >
              <div tw="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <div tw="flex flex-col gap-1">
                  <Label htmlFor="salary-min">
                    {rangeEnabled ? 'Minimum amount' : 'Amount'}
                  </Label>
                  <div tw="flex items-center gap-3">
                    <div tw="shrink-0">
                      <Checkbox
                        key={String(rangeEnabled)}
                        label="Range"
                        value={rangeEnabled}
                        onChange={handleRangeChange}
                      />
                    </div>
                    <Input
                      id="salary-min"
                      data-testid="salary-min-input"
                      value={salaryMin}
                      placeholder="45.000"
                      ariaInvalid={!!errors.salary_min}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setSalaryMin(nextValue);
                        applySalaryChange({ nextSalaryMin: nextValue });
                      }}
                    />
                  </div>
                  {errors.salary_min && <Error>{errors.salary_min}</Error>}
                </div>

                {rangeEnabled ? (
                  <div tw="flex flex-col gap-1">
                    <Label htmlFor="salary-max">Maximum amount</Label>
                    <Input
                      id="salary-max"
                      data-testid="salary-max-input"
                      value={salaryMax}
                      placeholder="55.000"
                      ariaInvalid={!!errors.salary_max}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setSalaryMax(nextValue);
                        applySalaryChange({ nextSalaryMax: nextValue });
                      }}
                    />
                    {errors.salary_max && <Error>{errors.salary_max}</Error>}
                  </div>
                ) : (
                  <div
                    tw="hidden xl:flex flex-col gap-1 invisible pointer-events-none"
                    aria-hidden="true"
                  >
                    <Label htmlFor="salary-max-placeholder">
                      Maximum amount
                    </Label>
                    <Input
                      id="salary-max-placeholder"
                      value=""
                      placeholder="55.000"
                      disabled
                    />
                  </div>
                )}

                <div tw="flex flex-col gap-1">
                  <Label htmlFor="salary-currency">Currency</Label>
                  <Dropdown
                    searchable={false}
                    label={currency}
                    value={currency}
                    items={CURRENCY_OPTIONS}
                    testId="salary-currency-select"
                    onChange={(key) => {
                      const nextCurrency = String(key);
                      setCurrency(nextCurrency);
                      applySalaryChange({ nextCurrency });
                    }}
                  />
                </div>

                <div tw="flex flex-col gap-1">
                  <Label htmlFor="salary-pay-period">Pay period</Label>
                  <Dropdown
                    searchable={false}
                    label={
                      PAY_PERIOD_OPTIONS.find(({ key }) => key === payPeriod)
                        ?.label
                    }
                    value={payPeriod}
                    items={PAY_PERIOD_OPTIONS}
                    testId="salary-pay-period-select"
                    onChange={(key) => {
                      const nextPayPeriod = key as PayPeriod;
                      setPayPeriod(nextPayPeriod);
                      applySalaryChange({ nextPayPeriod });
                    }}
                  />
                </div>
              </div>

              <div tw="flex flex-col gap-1">
                <div tw="flex items-center justify-between gap-3">
                  <Label htmlFor="salary-note">Optional note</Label>
                  <button
                    type="button"
                    tw="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-400 bg-gray-400 text-white shadow-sm transition hover:bg-gray-500 focus:outline-none"
                    aria-label="Remove salary"
                    title="Remove salary"
                    onClick={handleRemoveSalary}
                    data-testid="salary-remove-button"
                  >
                    <Trash size={18} weight="bold" />
                  </button>
                </div>
                <Input
                  id="salary-note"
                  data-testid="salary-note-input"
                  value={salaryNote}
                  placeholder="Depending on experience"
                  onChange={(e) => {
                    const nextSalaryNote = e.target.value;
                    setSalaryNote(nextSalaryNote);
                    applySalaryChange({ nextSalaryNote });
                  }}
                />
              </div>

              <aside tw="xl:col-span-2 flex flex-col gap-1 rounded border border-gray-200 bg-gray-50 px-3 py-2">
                <p tw="text-xs font-semibold uppercase text-gray-400">
                  Live preview
                </p>
                <p
                  tw="text-sm font-medium text-gray-800"
                  data-testid="salary-live-preview"
                >
                  {previewSalary ||
                    (Object.keys(errors).length
                      ? 'Fix salary fields to preview.'
                      : 'Salary preview will appear here.')}
                </p>
              </aside>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SalaryEditor;
